/** All times are presented in the clinic's time zone, whatever the browser's settings. */
export const CLINIC_TIME_ZONE = 'Europe/Warsaw';

const dateTime = new Intl.DateTimeFormat('pl-PL', {
  timeZone: CLINIC_TIME_ZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});
const time = new Intl.DateTimeFormat('pl-PL', { timeZone: CLINIC_TIME_ZONE, hour: '2-digit', minute: '2-digit' });
const day = new Intl.DateTimeFormat('pl-PL', { timeZone: CLINIC_TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long' });
const isoDate = new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });

export const formatDateTime = (value: string | Date) => dateTime.format(new Date(value));
export const formatTime = (value: string | Date) => time.format(new Date(value));
export const formatDay = (value: string | Date) => day.format(new Date(value));

/** yyyy-MM-dd of the instant in the clinic time zone. */
export const clinicDate = (value: string | Date) => isoDate.format(new Date(value));

/** Adds days to a yyyy-MM-dd date (calendar arithmetic, no time zone involved). */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const result = new Date(Date.UTC(y, m - 1, d + days));
  return result.toISOString().slice(0, 10);
}

/** Monday of the week containing a yyyy-MM-dd date. */
export function weekStart(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(date, -((weekday + 6) % 7));
}

export function groupByClinicDay<T extends { startsAt: string }>(items: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = clinicDate(item.startsAt);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export const DAY_NAMES = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];

export const STATUS_LABELS: Record<string, string> = {
  Pending: 'Oczekuje na potwierdzenie',
  Confirmed: 'Potwierdzona',
  Cancelled: 'Odwołana',
  Completed: 'Odbyta',
  NoShow: 'Nieobecność',
};

export const MODE_LABELS: Record<string, string> = { InPerson: 'Stacjonarnie', Online: 'Online' };

export const PAYMENT_LABELS: Record<string, string> = {
  NotRequired: '',
  Awaiting: 'Nieopłacona',
  Paid: 'Opłacona',
  Refunded: 'Płatność zwrócona',
};

/** Payment state of a visit for staff views: a visit marked as paid by hand counts as paid. */
export function paymentState(a: { paymentStatus: string; paidManuallyAt?: string | null }): string {
  return a.paidManuallyAt ? 'Paid' : a.paymentStatus;
}

/** Short payment state of a visit for staff views; null when it is paid at the clinic (nothing to track online). */
export function paymentBadge(a: { paymentStatus: string; paidManuallyAt?: string | null }): string | null {
  return PAYMENT_LABELS[paymentState(a)] || null;
}

/** 150 → "150 zł", 99.5 → "99,50 zł" */
export function formatPrice(value: number): string {
  return `${new Intl.NumberFormat('pl-PL', { minimumFractionDigits: Number.isInteger(value) ? 0 : 2, maximumFractionDigits: 2 }).format(value)} zł`;
}

export const ROLE_LABELS: Record<string, string> = {
  Admin: 'Administrator',
  Reception: 'Recepcja',
  Therapist: 'Terapeuta',
  Patient: 'Pacjent',
};

/** "HH:mm:ss" → "HH:mm" */
export const shortTime = (value: string) => value.slice(0, 5);

/** Current time captured once per component instance (keeps render pure). */
export function nowOnce(): number {
  return Date.now();
}

/** Same wording as the Excel export (VisitReportLabels on the server). */
export const OUTCOME_LABELS: Record<string, string> = {
  Scheduled: 'Zaplanowana',
  AwaitingConfirmation: 'Oczekuje na potwierdzenie',
  ToSettle: 'Do rozliczenia (nieoznaczona)',
  Completed: 'Odbyła się',
  NoShow: 'Nie odbyła się – nieobecność pacjenta',
  CancelledByPatient: 'Odwołana przez pacjenta',
  CancelledByTherapist: 'Odwołana przez terapeutę',
  CancelledByClinic: 'Odwołana przez placówkę',
  CancelledNonPayment: 'Odwołana – brak płatności',
};

/** Countdown in minutes and seconds: 125 → "2:05". */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
